/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * BLOCO 3 — Tela Informativa de Módulo Operacional Bloqueado
 * Exibida quando o período gratuito de 30 dias de uma Arena sem quadras expirou
 * e o Plano Operação ainda não foi contratado.
 */

import React, { useState } from "react";
import {
  Lock,
  Sparkles,
  CheckCircle2,
  ArrowLeft,
  DollarSign,
  ShieldCheck,
  ShoppingBag,
  TrendingUp,
  Receipt,
  Package,
  Layers,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { EntitlementService } from "../../services/commercial/EntitlementService";

interface OperationalModuleBlockedViewProps {
  arenaName?: string;
  arenaId?: string;
  ownerUid?: string;
  onBack?: () => void;
  onSuccessActivation?: () => void;
  onToast?: (msg: string, type: "success" | "error") => void;
}

export const OperationalModuleBlockedView: React.FC<OperationalModuleBlockedViewProps> = ({
  arenaName = "Sua Arena",
  arenaId = "",
  ownerUid = "",
  onBack,
  onSuccessActivation,
  onToast,
}) => {
  const [loading, setLoading] = useState(false);
  const monthlyPrice = EntitlementService.getOperationOnlyPrice();

  const handleActivatePlan = async () => {
    if (!arenaId) {
      if (onToast) onToast("Identificador da Arena não localizado para ativação.", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await EntitlementService.activateOperationOnlyPlan(arenaId, ownerUid);
      if (res.success) {
        if (onToast) onToast("Plano Operação ativado com sucesso! Módulos liberados.", "success");
        if (onSuccessActivation) {
          onSuccessActivation();
        } else {
          // Atualiza a página para refletir o novo entitlement
          window.location.reload();
        }
      } else {
        if (onToast) onToast(res.message || "Erro ao ativar o Plano Operação.", "error");
      }
    } catch (err: any) {
      if (onToast) onToast(err.message || "Falha na comunicação com o servidor.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div className="max-w-2xl w-full bg-theme-surface border border-theme rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
        
        {/* CABEÇALHO DO BLOQUEIO */}
        <div className="text-center space-y-3">
          <div className="inline-flex p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-500 mb-2">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-theme-primary tracking-tight">
            Seu período gratuito de 30 dias terminou.
          </h2>
          <p className="text-sm sm:text-base text-theme-secondary font-medium max-w-lg mx-auto">
            Assine o <strong className="text-amber-500 font-bold">Plano Operação</strong> para continuar utilizando os módulos operacionais de <span className="text-theme-primary font-semibold">{arenaName}</span>.
          </p>
        </div>

        {/* CARD DO PLANO OPERAÇÃO */}
        <div className="bg-gradient-to-br from-theme-surface-secondary/80 to-theme-surface-secondary/40 border border-amber-500/40 rounded-2xl p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-theme/60 pb-5">
            <div>
              <span className="text-[11px] font-black uppercase tracking-wider text-amber-500 bg-amber-500/10 px-2.5 py-1 rounded-md border border-amber-500/20">
                Produto Comercial Exclusivo
              </span>
              <h3 className="text-xl font-black text-theme-primary mt-2">
                Plano Operação
              </h3>
              <p className="text-xs text-theme-muted mt-0.5">
                Ideal para arenas sem quadras ou em estruturação que utilizam a operação interna
              </p>
            </div>

            <div className="text-left sm:text-right">
              <div className="flex items-baseline gap-1">
                <span className="text-xs font-bold text-theme-muted">R$</span>
                <span className="text-3xl font-black text-theme-primary">{monthlyPrice.toFixed(0)}</span>
                <span className="text-xs font-bold text-theme-muted">,00/mês</span>
              </div>
              <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                Sem fidelidade • Cancele quando quiser
              </span>
            </div>
          </div>

          {/* BENEFÍCIOS DO PLANO OPERAÇÃO */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">Vendas & PDV Balcão:</strong> Registro de pedidos e comandas
              </span>
            </div>

            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">Controle de Estoque:</strong> Produtos, bebidas e alertas de reposição
              </span>
            </div>

            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">Gestão Financeira:</strong> Fluxo de caixa, despesas operacionais e extrato
              </span>
            </div>

            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">0% de Comissão:</strong> Mensalidade fixa sem cobranças adicionais
              </span>
            </div>

            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">Sem Quadras Obrigatórias:</strong> Não exige quadras cadastradas
              </span>
            </div>

            <div className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-theme-secondary">
                <strong className="text-theme-primary">Dados 100% Preservados:</strong> Nenhum registro ou histórico é apagado
              </span>
            </div>
          </div>
        </div>

        {/* AVISO DE PRESERVAÇÃO DE DADOS E TORNEIOS */}
        <div className="p-4 rounded-xl bg-theme-surface-secondary/40 border border-theme flex items-start gap-3 text-xs text-theme-muted">
          <ShieldCheck size={18} className="text-sky-500 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Seu login na plataforma permanece sempre ativo e seus dados continuam intactos e seguros.
            O módulo de Torneios também continua acessível normalmente.
          </p>
        </div>

        {/* AÇÕES */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="w-full sm:w-auto px-5 py-3 rounded-xl border border-theme hover:bg-theme-surface-secondary text-theme-secondary hover:text-theme-primary font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} />
              <span>Voltar ao Início</span>
            </button>
          ) : (
            <div />
          )}

          <button
            type="button"
            disabled={loading}
            onClick={handleActivatePlan}
            className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-black text-sm shadow-lg shadow-amber-500/20 transition cursor-pointer flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Ativando Plano Operação...</span>
              </>
            ) : (
              <>
                <Sparkles size={18} />
                <span>Assinar Plano Operação (R$ {monthlyPrice.toFixed(0)}/mês)</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
